import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import {
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Typography,
  Alert,
  Paper,
} from '@mui/material';

import {
  VideoCall as VideoCallIcon,
  LocationOn as LocationOnIcon,
} from '@mui/icons-material';
import { toast } from 'react-toastify';
import api from '../services/api';

interface Case {
  id: number;
  case_name: string;
  case_type: string;
  service_type?: { name: string; short_code: string };
  status: string;
  firm?: { name: string };
  creator?: { name: string };
}

const MeetingSelection: React.FC = () => {
  const { id } = useParams<{ id: string }>();

  const [caseData, setCaseData] = useState<Case | null>(null);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [showLocationDialog, setShowLocationDialog] = useState(false);
  const [meetingLocation, setMeetingLocation] = useState('');
  const [selectedType, setSelectedType] = useState<
    'virtual' | 'in_person' | null
  >(null);

  useEffect(() => {
    if (id) {
      fetchCaseDetails();
    }
  }, [id]);

  const fetchCaseDetails = async () => {
    setLoading(true);
    setError('');
    try {
      const res = await api.get(`/public/cases/${id}/info`);
      setCaseData(res.data);
    } catch (err: any) {
      setError('Failed to load case details');
    } finally {
      setLoading(false);
    }
  };

  const handleMeetingTypeSelection = (type: 'virtual' | 'in_person') => {
    setSelectedType(type);

    if (type === 'virtual') {
      handleSubmit(type);
    } else {
      setShowLocationDialog(true);
    }
  };

  const handleSubmit = async (type?: 'virtual' | 'in_person') => {
    const meetingType = type || selectedType;
    if (!meetingType) return;

    setSubmitting(true);
    try {
      const payload: any = {
        meeting_type: meetingType,
      };

      if (meetingType === 'in_person') {
        if (!meetingLocation.trim()) {
          toast.error('Please provide the meeting location');
          return;
        }
        payload.meeting_location = meetingLocation;
      }

      await api.post(`/cases/${id}/select-meeting-type`, payload);

      toast.success(
        meetingType === 'virtual'
          ? 'Virtual meeting requested. You will receive available dates within 24 hours.'
          : 'In-person meeting requested. PVE team will contact you shortly.'
      );

      // Redirect to a thank you page or close the window
      setTimeout(() => {
        window.close();
      }, 3000);
    } catch (err: any) {
      toast.error(
        err.response?.data?.message || 'Failed to submit meeting type selection'
      );
    } finally {
      setSubmitting(false);
      setShowLocationDialog(false);
    }
  };

  if (loading) {
    return (
      <Box
        display='flex'
        justifyContent='center'
        alignItems='center'
        minHeight='400px'
      >
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity='error'>{error}</Alert>
      </Box>
    );
  }

  if (!caseData) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity='warning'>Case not found</Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3, maxWidth: 800, mx: 'auto' }}>
      <Card>
        <CardHeader
          title='Meeting Type Selection'
          subheader={`Case: ${caseData.case_name} (${caseData.service_type?.short_code || 'N/A'})`}
        />
        <CardContent>
          <Typography variant='body1' sx={{ mb: 3 }}>
            Thank you for sending us your signed retainer. Please select your
            preferred meeting type for the vocational evaluation.
          </Typography>

          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
              gap: 3,
            }}
          >
            <Paper
              elevation={selectedType === 'virtual' ? 8 : 2}
              sx={{
                p: 3,
                cursor: 'pointer',
                border: selectedType === 'virtual' ? 2 : 1,
                borderColor:
                  selectedType === 'virtual' ? 'primary.main' : 'divider',
                '&:hover': { elevation: 4 },
              }}
              onClick={() => handleMeetingTypeSelection('virtual')}
            >
              <Box display='flex' alignItems='center' mb={2}>
                <VideoCallIcon color='primary' sx={{ mr: 1, fontSize: 32 }} />
                <Typography variant='h6'>Virtual Meeting</Typography>
              </Box>
              <Typography variant='body2' color='text.secondary' sx={{ mb: 2 }}>
                Evaluation will be conducted via Microsoft Teams. You will
                receive available dates within 24 hours.
              </Typography>
              <Typography variant='body2' color='primary'>
                • No travel required
                <br />
                • Flexible scheduling
                <br />• Standard evaluation process
              </Typography>
            </Paper>

            <Paper
              elevation={selectedType === 'in_person' ? 8 : 2}
              sx={{
                p: 3,
                cursor: 'pointer',
                border: selectedType === 'in_person' ? 2 : 1,
                borderColor:
                  selectedType === 'in_person' ? 'primary.main' : 'divider',
                '&:hover': { elevation: 4 },
              }}
              onClick={() => handleMeetingTypeSelection('in_person')}
            >
              <Box display='flex' alignItems='center' mb={2}>
                <LocationOnIcon
                  color='secondary'
                  sx={{ mr: 1, fontSize: 32 }}
                />
                <Typography variant='h6'>In-Person Meeting</Typography>
              </Box>
              <Typography variant='body2' color='text.secondary' sx={{ mb: 2 }}>
                Evaluation will be conducted at your preferred location. Please
                provide the address details.
              </Typography>
              <Typography variant='body2' color='secondary'>
                • At your office
                <br />
                • At opposing counsel&apos;s office
                <br />• At our office locations
              </Typography>
            </Paper>
          </Box>

          {/* {selectedType && (
            <Box sx={{ mt: 3, textAlign: 'center' }}>
              <Button
                variant="contained"
                size="large"
                onClick={() => handleSubmit()}
                disabled={submitting}
                startIcon={submitting ? <CircularProgress size={20} /> : <CheckCircleIcon />}
              >
                {submitting ? 'Submitting...' : `Confirm ${selectedType === 'virtual' ? 'Virtual' : 'In-Person'} Meeting`}
              </Button>
            </Box>
          )} */}
        </CardContent>
      </Card>

      {/* Location Dialog */}
      <Dialog
        open={showLocationDialog}
        onClose={() => setShowLocationDialog(false)}
        maxWidth='sm'
        fullWidth
      >
        <DialogTitle>Provide Meeting Location</DialogTitle>
        <DialogContent>
          <Typography variant='body2' sx={{ mb: 2 }}>
            Please provide the address where you would like the in-person
            evaluation to be conducted.
          </Typography>
          <TextField
            autoFocus
            margin='dense'
            label='Meeting Location Address'
            type='text'
            fullWidth
            variant='outlined'
            multiline
            rows={4}
            value={meetingLocation}
            onChange={e => setMeetingLocation(e.target.value)}
            placeholder='Enter the complete address for the in-person evaluation...'
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowLocationDialog(false)}>Cancel</Button>
          <Button
            onClick={() => handleSubmit()}
            disabled={!meetingLocation.trim() || submitting}
            variant='contained'
          >
            {submitting ? 'Submitting...' : 'Submit'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default MeetingSelection;
